import { useCallback, useEffect, useLayoutEffect, useRef } from "react";
import { anchorAt, offsetKeeping, resourcesInRows, rowsInRange } from "../engine/rowModel";
import { DAY_WIDTHS, WINDOW_QUANTUM_DAYS, quantizeRange, visibleDays } from "../engine/timelineGeometry";
import { HEADER_HEIGHT, NEXT_PAGE_ROWS, ROW_OVERSCAN_PX } from "../constants";
import { useRowModel, usePlannerEngine, useView } from "../plannerContext";

/**
 * Turns the scroll position into what the rest of the screen needs, and only tells them when it matters.
 *
 * Components read a window of days and a window of rows from the viewport store. The window is widened by a
 * whole viewport on each side and rounded to steps (a week of days, a row), so scrolling inside a step changes
 * nothing and nothing is rendered; only crossing a step does. The data controller is told which days and which
 * resources are on screen, so it can fetch what is missing, and the next page of resources is requested when the
 * last rows come into view.
 */
export default function useViewportSync(scrollerRef) {
  const engine = usePlannerEngine();
  const model = useRowModel();
  const viewType = useView((state) => state.viewType);
  const panelWidth = useView((state) => state.panelWidth);

  const latest = useRef({});
  latest.current = { model, viewType };
  const told = useRef(null);
  /** The row at the top of the screen and how far from the top it starts, as of the last update. */
  const anchor = useRef(null);

  const update = useCallback(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;

    const { model: currentModel, viewType: currentViewType } = latest.current;
    const { timeline, viewportStore, controller, dataStore, actions } = engine;
    const dayWidth = DAY_WIDTHS[currentViewType];
    const quantum = WINDOW_QUANTUM_DAYS[currentViewType];

    const visibleWidth = Math.max(0, scroller.clientWidth - engine.refs.panelWidth.current);
    const exact = visibleDays(timeline, scroller.scrollLeft, visibleWidth, dayWidth);
    const overscan = Math.max(quantum, exact.lastDay - exact.firstDay + 1);
    const window = quantizeRange(timeline, { firstDay: exact.firstDay - overscan, lastDay: exact.lastDay + overscan }, quantum);

    const bodyHeight = Math.max(0, scroller.clientHeight - HEADER_HEIGHT);
    const rows = rowsInRange(currentModel, scroller.scrollTop - ROW_OVERSCAN_PX, scroller.scrollTop + bodyHeight + ROW_OVERSCAN_PX);
    anchor.current = anchorAt(currentModel, scroller.scrollTop);

    viewportStore.setState((state) =>
      state.firstDay === window.firstDay && state.lastDay === window.lastDay && state.firstRow === rows.first && state.lastRow === rows.last
        ? state
        : { ...state, firstDay: window.firstDay, lastDay: window.lastDay, firstRow: rows.first, lastRow: rows.last }
    );

    // The controller only needs the days on screen, in steps, and the resources whose rows are near, and only when
    // they changed.
    const onScreen = quantizeRange(timeline, exact, 7);
    const resourceIds = resourcesInRows(currentModel, rows.first, rows.last);
    const signature = `${onScreen.firstDay}|${onScreen.lastDay}|${resourceIds.join(",")}`;
    if (signature !== told.current) {
      told.current = signature;
      controller.setViewport({ firstDay: onScreen.firstDay, lastDay: onScreen.lastDay, centerDay: Math.round((exact.firstDay + exact.lastDay) / 2), resourceIds });
    }

    const { hasMore, isLoadingMore, status } = dataStore.getState();
    if (status === "ready" && hasMore && !isLoadingMore && rows.last >= currentModel.rows.length - NEXT_PAGE_ROWS) actions.loadNextPage();
  }, [engine, scrollerRef]);

  // Scrolling is handled once per frame however many events the browser sends.
  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return undefined;

    let frame = 0;
    const onScroll = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        update();
      });
    };

    scroller.addEventListener("scroll", onScroll, { passive: true });
    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(onScroll) : null;
    observer?.observe(scroller);

    return () => {
      scroller.removeEventListener("scroll", onScroll);
      observer?.disconnect();
      if (frame) cancelAnimationFrame(frame);
    };
  }, [scrollerRef, update]);

  // When the rows change, the row at the top of the screen stays where it is: if rows above it grew because more
  // allocations arrived, the scroll position moves by as much, so the list does not jump under the user's eyes.
  useLayoutEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller || !anchor.current) return;

    const wanted = offsetKeeping(model, anchor.current);
    if (wanted !== null && Math.abs(wanted - scroller.scrollTop) >= 1) scroller.scrollTop = wanted;
  }, [model, scrollerRef]);

  // What is visible also changes when the rows, the view or the width of the left panel do, with no scrolling.
  useLayoutEffect(() => {
    update();
  }, [update, model, viewType, panelWidth]);

  return update;
}
